import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { navigate } from 'gatsby';
import { Button, Spinner } from '@printdeal/ui-library';
import { DELIVERY_TOOLS, hasReviewGroup, isReviewGroupLead } from '@printdeal/business-logic';
import { useReOrderLineItemsMutation } from '@printdeal/checkout-frontend';
import { Order } from '@printdeal/commercetools-v2/types';
import { SanitizedLineItem } from '../../../../../../types/order/Order';
import { ServiceFactory } from '../../../../../../services/ServiceFactory';
import useSystemPages from '../../../../../../hooks/useSystemPages';
import {
  dispatchAccountEvent,
  DispatchCategoriesAccount,
} from '../../../../../../helpers/dataLayerHelpers/accountDataLayerHelper';
import { reOrderDuplicateArtworkHelper } from '../../../../../../helpers/ReOrderDuplicateArtworkHelper';

interface SubmitReOrderButtonsProps {
  lineItems: SanitizedLineItem[];
  hasValidFiles: boolean;
  order: Order;
  dispatchEventCategory?: DispatchCategoriesAccount;
}

const SubmitReOrderButtons = ({
  lineItems,
  hasValidFiles,
  order,
  dispatchEventCategory = 'order_detail_interaction',
}: SubmitReOrderButtonsProps) => {
  const [cartPage] = useSystemPages('cart');
  const fileDeliveryApi = ServiceFactory.createFileDeliveryService();
  const reOrderLineItemsMutation = useReOrderLineItemsMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (reOrderLineItemsMutation.status === 'success' || reOrderLineItemsMutation.status === 'error') {
      setIsSubmitting(false);
      navigate(cartPage.path);
    }
  }, [cartPage.path, reOrderLineItemsMutation.status]);

  const countLineItems = lineItems
    .filter((lineItem) => isReviewGroupLead(lineItem) || !hasReviewGroup(lineItem)).length;

  const handleSubmit = async (reOrderWithFile: boolean) => {
    dispatchAccountEvent(dispatchEventCategory, 'submit_reorder');
    setIsSubmitting(true);
    const reOrderableOrder = {
      ...order,
      lineItems: lineItems.map((currentLineItem) => ({
        ...currentLineItem,
        custom: {
          ...currentLineItem.custom,
          fields: {
            ...currentLineItem.custom!.fields,
            userSelection: currentLineItem.custom!.fields.variableAttributes,
            editorType: currentLineItem.custom!.fields.editorType === DELIVERY_TOOLS.CHECKMATE
              ? DELIVERY_TOOLS.UPLOADER_TOOL : currentLineItem.custom!.fields.editorType,
          },
        },
      })),
    };

    reOrderLineItemsMutation.mutate({
      lineItemIds: lineItems.map((currentLineItem) => currentLineItem.id),
      order: reOrderableOrder,
      withFile: reOrderWithFile,
      artworks: reOrderWithFile ? await reOrderDuplicateArtworkHelper(lineItems, fileDeliveryApi) : undefined,
    });
  };

  return (
    <>
      {hasValidFiles && (
        <Button
          data-cy="reorderWithDesignsButton"
          disabled={isSubmitting}
          variant="primary"
          onClick={() => handleSubmit(true)}
        >
          {reOrderLineItemsMutation.isPending && (
            <Spinner
              variant="white"
              size="sm"
              screenReaderLabel="Loading..."
            />
          )}
          <FormattedMessage
            id="account.order.reorder.keep.designs"
            defaultMessage="{count, plural, one {Add and keep design} other {Add and keep all designs}}"
            values={{ count: countLineItems }}
          />
        </Button>
      )}
      <Button
        data-cy="reorderWithoutDesignsButton"
        disabled={isSubmitting}
        variant={hasValidFiles ? 'secondary' : 'primary'}
        onClick={() => handleSubmit(false)}
      >
        {reOrderLineItemsMutation.isPending && (
          <Spinner
            variant="dim"
            size="sm"
            screenReaderLabel="Loading..."
          />
        )}
        {hasValidFiles ? (
          <FormattedMessage
            id="account.order.reorder.remove.designs"
            defaultMessage="{count, plural, one {Add and remove design} other {Add and remove all designs}}"
            values={{ count: countLineItems }}
          />
        ) : (
          <FormattedMessage
            id="account.order.reorder.empty.designs"
            defaultMessage="{count, plural, one {Add item} other {Add all items}}"
            values={{ count: countLineItems }}
          />
        )}
      </Button>
    </>
  );
};

export default SubmitReOrderButtons;
